import {effect, Injectable, signal, WritableSignal} from '@angular/core';
import { HttpClient } from "@angular/common/http";
import {environment} from "../../../environments/environment";
import {Observable, tap} from "rxjs";
import {UserModel} from "../../models/resources/user.model";
import {LanguageService} from "../resources/language.service";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private static readonly STORAGE_KEY = 'auth_user';
  user: WritableSignal<UserModel | null>

  constructor(private http: HttpClient,
              private languageService: LanguageService) {
    this.user = signal(this.readStoredUser())

    effect(() => {
      if (this.user()) {
        localStorage.setItem(AuthService.STORAGE_KEY, JSON.stringify(this.toStoredUser(this.user()!)))
        if(this.user()?.language)
          this.languageService.activeLanguage.set(this.user()!.language!)
      } else
        localStorage.removeItem(AuthService.STORAGE_KEY)
    })
  }

  private readStoredUser(): UserModel | null {
    try {
      const raw = localStorage.getItem(AuthService.STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as Partial<UserModel>;
      if (!parsed?.token) {
        return null;
      }
      return {
        id: parsed.id ?? 0,
        role: parsed.role!,
        username: '',
        updatedAt: parsed.updatedAt ?? '',
        active: true,
        available: true,
        language: parsed.language,
        token: parsed.token,
      };
    } catch {
      return null;
    }
  }

  private toStoredUser(user: UserModel): Partial<UserModel> {
    return {
      id: user.id,
      updatedAt: user.updatedAt,
      role: user.role,
      language: user.language,
      token: user.token,
    };
  }

  static clearStoredAuth(): void {
    localStorage.removeItem(AuthService.STORAGE_KEY);
  }

  public login(username: string, password: string) {
    return this.http.post<{token: string}>(environment.apiUrl + '/login', {
      username,
      password
    })
      .pipe(
        tap((response: any) => {
          this.user.set(response);
        })
      );
  }

  public forgotPassword(email: string): Observable<{message: string}> {
    return this.http.post<{message: string}>(environment.apiUrl + '/forgot-password', { email });
  }

  public resetPassword(payload: { email: string; token: string; password: string; password_confirmation: string }): Observable<{message: string}> {
    return this.http.post<{message: string}>(environment.apiUrl + '/reset-password', payload);
  }

  public logout(): Observable<any> {
    return this.http.post<any>(environment.apiUrl + '/logout', {})
      // .pipe(
      //   tap(() => {
      //     this.token.set(null)
      //   })
      // )
  }

  public updateLanguage(languageId: number): Observable<UserModel> {
    return this.http.put<UserModel>(environment.apiUrl + `/users/${this.user()?.id}`, {
      languageId
    }).pipe(
      tap((updated) => {
        if (this.user())
          this.user.update(u => u ? { ...u, ...updated } : null);
      })
    );
  }
}
